import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '../hooks/useThunkDispatch';
import { debugSelectors } from '../selectors/debugSelectors';

/**
 * State for the soft navigation focus management.
 * @param skipSoftNavFocus - If true, the soft navigation focus management will be skipped.
 */

type SoftNavHistoryState = {
    skipSoftNavFocus?: boolean;
};

const createSoftNavFocusTargetNode = () => {
    const focusTarget = document.createElement('soft-nav-focus');
    document.body.prepend(focusTarget);
    return focusTarget;
};

const getSoftNavFocusTargetNode = () =>
    document.getElementsByTagName('soft-nav-focus')[0] as HTMLElement;

export const moveFocusAfterSoftNav = () => {
    const focusTarget = getSoftNavFocusTargetNode() || createSoftNavFocusTargetNode();
    focusTarget.setAttribute('tabindex', '-1');
    focusTarget.focus({ preventScroll: true });
    focusTarget.removeAttribute('tabindex');
};

/**
 * Simulate what MPA pages does when user is navigated to a new page.
 */
const RouteChangeFocusManagement = () => {
    const { pathname, state } = useLocation<SoftNavHistoryState>();

    const locationChanged = useRef(false);

    const debugA11yFocusOnSoftNavDisabled = useAppSelector(
        debugSelectors.selectDebugA11yFocusOnSoftNavDisabled,
    );

    useEffect(() => {
        if (debugA11yFocusOnSoftNavDisabled) {
            return;
        }

        if (locationChanged.current && !state?.skipSoftNavFocus) {
            moveFocusAfterSoftNav();
        } else {
            locationChanged.current = true;
        }
    }, [debugA11yFocusOnSoftNavDisabled, pathname, state?.skipSoftNavFocus]);

    return null;
};

export default RouteChangeFocusManagement;
